import { useQuery } from "@tanstack/react-query";

import type { QuoteSummary } from "~/features/ar-dashboard/schemas/dashboard-schemas.ts";
import { quoteSummarySchema } from "~/features/ar-dashboard/schemas/dashboard-schemas.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

import type { DashboardQueryParams } from "./dashboard-query-params.ts";
import { buildDashboardSearchParams } from "./dashboard-query-params.ts";

const emptyQuoteSummary: QuoteSummary = [];

type UseQuoteSummaryParams = Pick<
	DashboardQueryParams,
	| "ltlBrokerIds"
	| "primaryBrokerIds"
	| "customerIds"
	| "startDate"
	| "endDate"
	| "carrierId"
>;

export const useQuoteSummary = (params: UseQuoteSummaryParams) => {
	// The endpoint requires at least one of customerIds, ltlBrokerIds, or
	// primaryBrokerIds. Wait for the shared user filter to populate before
	// firing to avoid an initial 400 on load.
	const hasFilter =
		(params.ltlBrokerIds?.length ?? 0) > 0 ||
		(params.primaryBrokerIds?.length ?? 0) > 0 ||
		(params.customerIds?.length ?? 0) > 0;
	const enabled = Boolean(params.startDate && params.endDate && hasFilter);

	const { data, isLoading, isFetching, isSuccess, error, refetch } = useQuery({
		queryKey: [
			"customersQuoteSummary",
			params.ltlBrokerIds ?? [],
			params.primaryBrokerIds ?? [],
			params.customerIds ?? [],
			params.startDate ?? null,
			params.endDate ?? null,
			params.carrierId ?? null,
		],
		queryFn: async ({ signal }) => {
			const searchParams = buildDashboardSearchParams(params, {
				dateOnly: true,
			});
			const url = `${apiRoutes.CUSTOMERS_QUOTE_SUMMARY}?${searchParams.toString()}`;
			const result = await getAndValidate(url, quoteSummarySchema, {
				signal,
			});
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		enabled,
		staleTime: Infinity,
		gcTime: Infinity,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	// A manual refetch() bypasses the `enabled` guard, so wrap it to keep the
	// same filter requirement and avoid firing an empty-filter request (400)
	// from the dashboard Refresh button.
	const guardedRefetch = () => {
		if (!enabled) {
			return;
		}
		return refetch();
	};

	return {
		data: data ?? emptyQuoteSummary,
		isLoading,
		isFetching,
		isEnabled: enabled,
		isSuccess,
		error,
		refetch: guardedRefetch,
	};
};
